import { getEmailPattern } from '@/utils/commonUtils'
import i18n from '@/plugins/i18n/config.js'
import { isStatusUpdateDateValid } from '@/utils/statusUpdateDateUtils'

const { t } = i18n.global

class Validators {
  static repairFileStatusDateRules(minimum, maximum) {
    return [
      (value) =>
        isStatusUpdateDateValid(value, minimum, maximum) ||
        t('ERROR.INVALID_UPDATE_STATUS_DATE_ERROR')
    ]
  }

  static rentalFileStatusDateRules(minimum, maximum) {
    return [
      (value) =>
        isStatusUpdateDateValid(value, minimum, maximum) ||
        t('ERROR.INVALID_RENTAL_FILE_STATUS_UPDATE_DATE_ERROR')
    ]
  }

  static maxLengthRules(maxLength) {
    return [(value) => !value || value.length <= maxLength || t('ERROR_INPUT.MAX_LENGTH')]
  }

  static confirmPasswordRule(confirmPassword) {
    return [(value) => value === confirmPassword || t('ERROR_INPUT.PASSWORD.DIFFERENT_PASSWORD')]
  }

  static filesMaximumCountRule(currentLength, maxLength) {
    return [
      (value) =>
        !value ||
        value.length <= maxLength - currentLength ||
        t('ERROR_INPUT.MAX_FILES', {
          max: maxLength - currentLength
        })
    ]
  }

  static requiredRules = [(value) => !!value || t('ERROR_INPUT.EMPTY')]

  static trimmedRequiredRules = [
    (value) => (value && value.trim() !== '') || t('ERROR_INPUT.EMPTY')
  ]

  static requiredMultipleRules = [(value) => value.length > 0 || t('ERROR_INPUT.EMPTY')]

  static isBooleanRules = [(value) => typeof value === 'boolean' || t('ERROR_INPUT.EMPTY')]

  static emailRules = [(value) => getEmailPattern().test(value) || t('ERROR_INPUT.EMAIL.INVALID')]

  static multipleEmailRules = [
    (value) => {
      for (const email of value) {
        for (const rule of this.emailRules) {
          const result = rule(email)
          if (result !== true) {
            return result
          }
        }
      }

      return true
    }
  ]

  static semicolonSeparatedEmailRules = [
    (value) => {
      if (!value || value.trim() === '') {
        return t('ERROR_INPUT.EMPTY')
      }

      const emailList = value
        .split(';')
        .map((email) => email.trim())
        .filter((email) => email !== '')

      if (emailList.length === 0) {
        return t('ERROR_INPUT.EMPTY')
      }

      for (const email of emailList) {
        for (const rule of this.emailRules) {
          const result = rule(email)
          if (result !== true) {
            return result
          }
        }
      }

      return true
    }
  ]

  static optionalSemicolonSeparatedEmailRules = [
    (value) => {
      if (!value || value.trim() === '') {
        return true
      }

      const emailList = value
        .split(';')
        .map((email) => email.trim())
        .filter((email) => email !== '')

      for (const email of emailList) {
        for (const rule of this.emailRules) {
          const result = rule(email)
          if (result !== true) {
            return result
          }
        }
      }

      return true
    }
  ]

  static passwordRules = [
    (value) => !!value || t('ERROR_INPUT.PASSWORD.EMPTY'),
    (value) => value.length >= 12 || t('ERROR_INPUT.PASSWORD.ERROR_LENGTH'),
    (value) => Validators._containsUppercase(value) || t('ERROR_INPUT.PASSWORD.ERROR_UPPERCASE'),
    (value) => Validators._containsLowercase(value) || t('ERROR_INPUT.PASSWORD.ERROR_LOWERCASE'),
    (value) => Validators._containsDigit(value) || t('ERROR_INPUT.PASSWORD.ERROR_DIGIT'),
    (value) =>
      Validators._containsSpecialChar(value) || t('ERROR_INPUT.PASSWORD.ERROR_SPECIAL_CHAR')
  ]

  static numberRules = [
    (value) =>
      value !== '' ||
      (value !== null && value.length > 0) ||
      !isNaN(value) ||
      t('ERROR_INPUT.NOT_A_NUMBER'),
    (value) => value >= 0 || t('ERROR_INPUT.POSITIVE_VALUE')
  ]

  static integerRules = [(value) => !isNaN(value) || t('ERROR_INPUT.NOT_A_NUMBER')]

  static marginRules = [
    (value) => !isNaN(parseFloat(value)) || t('ERROR_INPUT.NOT_A_NUMBER'),
    (value) => value >= 0 || t('ERROR_INPUT.POSITIVE_VALUE'),
    (value) => value <= 60 || t('ERROR_INPUT.MARGIN_MAX_VALUE')
  ]

  static max10CharRules = Validators.maxLengthRules(10)

  static max12CharRules = Validators.maxLengthRules(12)

  static max25CharRules = Validators.maxLengthRules(25)

  static max50CharRules = Validators.maxLengthRules(50)

  static max100CharRules = Validators.maxLengthRules(100)

  static max150CharRules = Validators.maxLengthRules(150)

  static max250CharRules = Validators.maxLengthRules(250)

  static max255CharRules = Validators.maxLengthRules(255)

  static worksiteCodeRules = [
    (value) => !value || /^[A-Z0-9 ]+$/.test(value) || t('ERROR_INPUT.WORKSITE_CODE')
  ]

  static isNumberRules = [(value) => !isNaN(parseFloat(value)) || t('ERROR_INPUT.NOT_A_NUMBER')]

  static positiveNumberRules = [(value) => value >= 0 || t('ERROR_INPUT.POSITIVE_VALUE')]

  static mobileNumberRules = [(value) => /^[67]/.test(value) || t('ERROR_INPUT.PHONE_NUMBER')]

  static _containsUppercase(password) {
    return /[A-Z]/.test(password)
  }

  static _containsLowercase(password) {
    return /[a-z]/.test(password)
  }

  static _containsDigit(password) {
    return /[0-9]/.test(password)
  }

  static _containsSpecialChar(password) {
    return /[!@#$%^&*(),.?":{}|<>]/.test(password)
  }
}

export default Validators
